import { type Audit, AuditStatus } from '@compass/client/types/audit';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Copy } from 'lucide-react';
import React from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { copyToClipboard } from '@/utils/clipboard';

import type { PopoverContentProps, TabLabelContext, TabTypeHandler } from './types';

const RECOMMENDATION_LABEL: Record<string, string> = {
  ESCALATE_TO_HUMAN: 'Escalate to Human',
  AUTO_APPROVE: 'Auto Approve',
};

const recommendationLabel = (value: string): string =>
  RECOMMENDATION_LABEL[value.toUpperCase()] ?? value;

const recommendationBadgeVariant = (value: string): 'warning' | 'success' | 'secondary' => {
  const normalized = value.toUpperCase();
  if (normalized === 'ESCALATE_TO_HUMAN') return 'warning';
  if (normalized === 'AUTO_APPROVE') return 'success';
  return 'secondary';
};

const getLabel = (_data: Audit, context: TabLabelContext): string => {
  // Suffix the position only when the case has more than one audit, so a
  // single-audit case keeps the original "AI Audit" label.
  return context.totalOfType > 1 ? `AI Audit ${context.attemptNumber}` : 'AI Audit';
};

const AUDIT_STATUS_LABEL: Record<AuditStatus, string> = {
  [AuditStatus.AUDIT_STATUS_UNSPECIFIED]: 'Unspecified',
  [AuditStatus.AUDIT_STATUS_IN_PROGRESS]: 'In progress',
  [AuditStatus.AUDIT_STATUS_COMPLETED]: 'Completed',
  [AuditStatus.AUDIT_STATUS_FAILED]: 'Failed',
  [AuditStatus.AUDIT_STATUS_SKIPPED]: 'Skipped',
  [AuditStatus.AUDIT_STATUS_CANCELLED]: 'Cancelled',
};

const AiAuditPopoverContent: React.FC<PopoverContentProps<Audit>> = ({ data: audit }) => {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between gap-8 items-center">
        <span className="text-muted-foreground">Audit ID:</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{audit.id}</span>
          {audit.id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => audit.id && copyToClipboard(audit.id)}
              className="h-6 w-6 p-0"
            >
              <Copy className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex justify-between gap-8">
        <span className="text-muted-foreground">Type:</span>
        <span>AI Audit</span>
      </div>
      {audit.status !== undefined && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Status:</span>
          <span>{AUDIT_STATUS_LABEL[audit.status] ?? 'Unknown'}</span>
        </div>
      )}
      {audit.analyzed_at && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Analyzed at:</span>
          <FormattedDateTime value={audit.analyzed_at} />
        </div>
      )}
      {audit.recommendation && (
        <div className="flex justify-between gap-8 items-center">
          <span className="text-muted-foreground">Recommendation:</span>
          <Badge variant={recommendationBadgeVariant(audit.recommendation)}>
            {recommendationLabel(audit.recommendation)}
          </Badge>
        </div>
      )}
      {audit.created_at && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Created at:</span>
          <FormattedDateTime value={audit.created_at} />
        </div>
      )}
      {/* failure_reason is populated for both FAILED and SKIPPED runs (SKIPPED =
          ineligible at the eligibility gate). Show the same reason block for
          either status, with a status-appropriate label and tone — red for a
          genuine failure, neutral for an informational skip. */}
      {(audit.status === AuditStatus.AUDIT_STATUS_FAILED ||
        audit.status === AuditStatus.AUDIT_STATUS_SKIPPED) &&
        audit.failure_reason && (
          <div className="bg-secondary p-2 rounded text-xs">
            <div className="text-muted-foreground mb-1">
              {audit.status === AuditStatus.AUDIT_STATUS_FAILED
                ? 'Failure reason:'
                : 'Skipped reason:'}
            </div>
            <div
              className={`whitespace-pre-wrap max-w-[400px] max-h-20 overflow-auto ${
                audit.status === AuditStatus.AUDIT_STATUS_FAILED
                  ? 'text-destructive'
                  : 'text-foreground'
              }`}
            >
              {audit.failure_reason}
            </div>
          </div>
        )}
    </div>
  );
};

export const aiAuditHandler: TabTypeHandler<Audit> = {
  isDisabled: false,
  getLabel,
  getStatusLabel: () => 'AI Audit',
  PopoverContent: AiAuditPopoverContent,
};
